import styled from 'styled-components';
import { motion } from 'framer-motion';
import IconSuccess from '../../assets/Images/icons/icon-success';
import IconFail from '../../assets/Images/icons/icon-fail';
import IconImportSuccess from '../../assets/Images/icons/icon-import-success';
import IconImportFails from '../../assets/Images/icons/icon-import-fails';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Some basic styles
 */

// Notice how we're wrapping 'motion-div'
// styled-components generates a className and injects it into the motion.div

const Base = styled(motion.div)`
    display: flex;
    color: #171717;
    border-radius: 18px;
    background-color: #ffffff;
    overflow: hidden;
    width: 450px;
    margin-bottom: 32;
    box-shadow: 3px 3px 15px 0px rgba(0, 0, 0, 0.25);
    margin-top: 1em;
    z-index: 9999;
`;

const Description = styled.div`
    padding: 0.5rem 2rem;
    line-height: 1.3;
    font-weight: 400;
    font-size: 14px;
    color: #65707a;
`;

const Title = styled.div`
    line-height: 1.3;
    font-weight: 400;
    font-size: 22px;
    margin-bottom: 4px;
`;

function getIcon(notificationType) {
    if (notificationType === 'error') {
        return {
            Icon: <IconFail />,
        };
    }

    if (notificationType === 'warning') {
        return {
            Icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-yellow-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                </svg>
            ),
        };
    }

    if (notificationType === 'alert') {
        return {
            Icon: (
                <svg
                    width="150"
                    height="108"
                    viewBox="0 0 150 108"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g clipPath="url(#clip0_2118_17672)">
                        <path
                            d="M117.893 9.71776C117.514 6.949 117.039 3.97632 115.079 1.97761C114.457 1.34342 113.713 0.841276 112.891 0.501456C112.068 0.161636 111.186 -0.00882479 110.296 0.00035175C109.406 0.00952829 108.527 0.198154 107.712 0.554852C106.898 0.911549 106.164 1.42893 105.555 2.07581C104.128 3.5923 103.498 5.72168 103.44 7.79947C103.383 9.87725 103.847 11.932 104.363 13.9462C107.089 14.0004 109.796 13.4697 112.299 12.3899C112.912 12.0916 113.549 11.8427 114.202 11.6456C114.863 11.4779 115.334 11.8999 115.949 12.194L116.287 11.55C116.563 12.0624 117.419 11.9105 117.73 11.4178C118.04 10.9251 117.972 10.2941 117.893 9.71776Z"
                            fill="#2F2E41"
                        />
                        <path
                            d="M106.503 21.4081L107.293 17.3898L101.373 14.8364L90.7887 9.78487C90.7149 9.31613 90.5001 8.88074 90.1725 8.53631C89.845 8.19187 89.4202 7.95462 88.9544 7.85593C88.4885 7.75725 88.0036 7.80178 87.5637 7.98363C87.1239 8.16549 86.7498 8.4761 86.4909 8.87438C86.2321 9.27266 86.1007 9.73984 86.1142 10.2141C86.1277 10.6884 86.2854 11.1474 86.5664 11.5305C86.8475 11.9135 87.2386 12.2025 87.6881 12.3593C88.1376 12.5161 88.6243 12.5332 89.0838 12.4084L99.1611 18.6042L106.503 21.4081Z"
                            fill="#FFB8B8"
                        />
                        <path
                            d="M102.529 20.7282L106.817 16.3613L106.902 16.387C106.955 16.4031 112.283 18.0212 115.679 19.6654C116.063 19.8371 116.405 20.0901 116.68 20.4068C116.956 20.7236 117.159 21.0964 117.275 21.4993C117.444 22.0634 117.444 22.6645 117.275 23.2286C117.106 23.7928 116.775 24.2955 116.323 24.675C115.859 25.0891 115.285 25.3613 114.669 25.4589C114.053 25.5566 113.423 25.4755 112.852 25.2255L102.529 20.7282Z"
                            fill="#7B67FB"
                        />
                        <path
                            d="M102.516 39.9274L114.573 43.1372L117.008 31.7896L105.941 28.377L102.516 39.9274Z"
                            fill="#FFB8B8"
                        />
                        <path
                            d="M115.922 36.8474L104.144 34.1488L101.888 28.7027C101.705 27.9253 101.749 27.1118 102.017 26.3589C102.284 25.606 102.763 24.9456 103.397 24.4562L107.7 20.8366L110.007 17.9006L109.756 16.3447C109.711 16.0337 109.776 15.7169 109.941 15.449C110.106 15.181 110.359 14.9788 110.658 14.8772L113.732 13.7788C114.072 13.6521 114.448 13.6589 114.783 13.7977C115.118 13.9366 115.387 14.197 115.537 14.5264L115.863 15.2777C115.863 15.2777 117.253 15.9295 118.037 16.2961C118.44 16.4788 118.8 16.7443 119.094 17.0753C119.387 17.4062 119.607 17.7952 119.739 18.2166C120.938 22.0794 120.615 26.2525 118.834 29.8861L115.922 36.8474Z"
                            fill="#7B67FB"
                        />
                        <path
                            d="M11.1734 94.6595L5.4784 93.1413C4.64104 90.1717 4.54395 87.0433 5.19558 84.0278C7.68013 88.9208 14.3529 89.8434 18.3058 93.659C19.5296 94.8585 20.4667 96.3169 21.0478 97.9263C21.6288 99.5356 21.8389 101.255 21.6625 102.956L23.0267 106.893C20.2576 106.784 17.5451 106.078 15.0761 104.824C12.6072 103.57 10.4405 101.798 8.72534 99.6292C7.77523 98.4031 6.98387 97.0627 6.36996 95.6396C8.75297 95.2918 11.1734 94.6595 11.1734 94.6595Z"
                            fill="#F2F2F2"
                        />
                        <path
                            d="M81.9255 108H26.1072C24.6113 107.998 23.1771 107.406 22.1193 106.352C21.0615 105.298 20.4665 103.869 20.4648 102.379V28.7458C20.4665 27.2554 21.0615 25.8266 22.1193 24.7728C23.1771 23.719 24.6113 23.1262 26.1072 23.1245H81.9255C83.4214 23.1262 84.8556 23.719 85.9133 24.7728C86.9711 25.8266 87.5661 27.2554 87.5678 28.7458V102.379C87.5661 103.869 86.9711 105.298 85.9133 106.352C84.8556 107.406 83.4214 107.998 81.9255 108Z"
                            fill="#E6E6E6"
                        />
                        <path
                            d="M60.6225 103.442H30.032C28.6699 103.441 27.3641 102.901 26.401 101.941C25.4378 100.982 24.8961 99.6808 24.8945 98.3238V32.8004C24.8961 31.4434 25.4378 30.1425 26.401 29.1829C27.3641 28.2234 28.67 27.6837 30.032 27.6821H78.0031C79.3652 27.6837 80.671 28.2234 81.6342 29.1829C82.5973 30.1425 83.1391 31.4434 83.1406 32.8004V81.0084C83.1339 86.9562 80.7592 92.6584 76.5377 96.8641C72.3162 101.07 66.5926 103.436 60.6225 103.442Z"
                            fill="white"
                        />
                        <path
                            d="M73.2166 40.2614H34.8164C34.652 40.2617 34.4891 40.2296 34.337 40.1671C34.185 40.1046 34.0468 40.0128 33.9304 39.8971C33.814 39.7814 33.7216 39.644 33.6584 39.4927C33.5953 39.3414 33.5627 39.1792 33.5625 39.0153C33.5623 38.8515 33.5945 38.6892 33.6572 38.5378C33.72 38.3863 33.8121 38.2487 33.9282 38.1326C34.0443 38.0166 34.1823 37.9246 34.3342 37.8617C34.486 37.7988 34.6488 37.7663 34.8133 37.7661H73.2166C73.5487 37.7657 73.8674 37.8968 74.1026 38.1304C74.3378 38.3641 74.4701 38.6813 74.4705 39.0122C74.4709 39.3431 74.3394 39.6606 74.1048 39.8949C73.8703 40.1292 73.5519 40.261 73.2197 40.2614H73.2166Z"
                            fill="#65707A"
                        />
                        <path
                            d="M73.2171 45.1918H34.8169C34.4847 45.1918 34.1662 45.0603 33.9313 44.8263C33.6964 44.5923 33.5645 44.2749 33.5645 43.944C33.5645 43.6131 33.6964 43.2957 33.9313 43.0617C34.1662 42.8277 34.4847 42.6963 34.8169 42.6963H73.2171C73.5492 42.6963 73.8678 42.8277 74.1026 43.0617C74.3375 43.2957 74.4695 43.6131 74.4695 43.944C74.4695 44.2749 74.3375 44.5923 74.1026 44.8263C73.8678 45.0603 73.5492 45.1918 73.2171 45.1918Z"
                            fill="#65707A"
                        />
                        <path
                            d="M51.4624 50.1219H34.8169C34.4847 50.1219 34.1662 49.9905 33.9313 49.7565C33.6964 49.5225 33.5645 49.2051 33.5645 48.8742C33.5645 48.5433 33.6964 48.2259 33.9313 47.9919C34.1662 47.7579 34.4847 47.6265 34.8169 47.6265H51.4624C51.7946 47.6265 52.1132 47.7579 52.348 47.9919C52.5829 48.2259 52.7149 48.5433 52.7149 48.8742C52.7149 49.2051 52.5829 49.5225 52.348 49.7565C52.1132 49.9905 51.7946 50.1219 51.4624 50.1219Z"
                            fill="#65707A"
                        />
                        <path
                            d="M73.2166 60.4475H34.8164C34.4843 60.4479 34.1656 60.3168 33.9304 60.0832C33.6953 59.8495 33.5629 59.5323 33.5625 59.2014C33.5621 58.8705 33.6936 58.553 33.9282 58.3187C34.1628 58.0844 34.4811 57.9526 34.8133 57.9521H73.2166C73.5487 57.9517 73.8674 58.0828 74.1026 58.3165C74.3378 58.5502 74.4701 58.8674 74.4705 59.1983C74.4709 59.5292 74.3394 59.8467 74.1048 60.081C73.8703 60.3152 73.5519 60.4471 73.2197 60.4475H73.2166Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M73.2171 65.3778H34.8169C34.4847 65.3778 34.1662 65.2463 33.9313 65.0123C33.6964 64.7783 33.5645 64.461 33.5645 64.1301C33.5645 63.7991 33.6964 63.4818 33.9313 63.2478C34.1662 63.0138 34.4847 62.8823 34.8169 62.8823H73.2171C73.5492 62.8823 73.8678 63.0138 74.1026 63.2478C74.3375 63.4818 74.4695 63.7991 74.4695 64.1301C74.4695 64.461 74.3375 64.7783 74.1026 65.0123C73.8678 65.2463 73.5492 65.3778 73.2171 65.3778Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M51.4624 70.308H34.8169C34.4847 70.308 34.1662 70.1765 33.9313 69.9425C33.6964 69.7085 33.5645 69.3912 33.5645 69.0602C33.5645 68.7293 33.6964 68.4119 33.9313 68.178C34.1662 67.944 34.4847 67.8125 34.8169 67.8125H51.4624C51.7946 67.8125 52.1132 67.944 52.348 68.178C52.5829 68.4119 52.7149 68.7293 52.7149 69.0602C52.7149 69.3912 52.5829 69.7085 52.348 69.9425C52.1132 70.1765 51.7946 70.308 51.4624 70.308Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M73.2166 80.6335H34.8164C34.4843 80.6339 34.1656 80.5029 33.9304 80.2692C33.6953 80.0355 33.5629 79.7183 33.5625 79.3874C33.5621 79.0565 33.6936 78.739 33.9282 78.5047C34.1628 78.2704 34.4811 78.1386 34.8133 78.1382H73.2166C73.5487 78.1378 73.8674 78.2688 74.1026 78.5025C74.3378 78.7362 74.4701 79.0534 74.4705 79.3843C74.4709 79.7152 74.3394 80.0327 74.1048 80.267C73.8703 80.5013 73.5519 80.6331 73.2197 80.6335H73.2166Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M73.2171 85.5633H34.8169C34.4847 85.5633 34.1662 85.4319 33.9313 85.1979C33.6964 84.9639 33.5645 84.6465 33.5645 84.3156C33.5645 83.9847 33.6964 83.6673 33.9313 83.4333C34.1662 83.1993 34.4847 83.0679 34.8169 83.0679H73.2171C73.5492 83.0679 73.8678 83.1993 74.1026 83.4333C74.3375 83.6673 74.4695 83.9847 74.4695 84.3156C74.4695 84.6465 74.3375 84.9639 74.1026 85.1979C73.8678 85.4319 73.5492 85.5633 73.2171 85.5633Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M51.462 90.4935H34.8164C34.4847 90.4929 34.1667 90.3611 33.9324 90.1272C33.698 89.8933 33.5664 89.5763 33.5664 89.2458C33.5664 88.9153 33.698 88.5983 33.9324 88.3643C34.1667 88.1304 34.4847 87.9987 34.8164 87.998H51.462C51.7937 87.9987 52.1117 88.1304 52.346 88.3643C52.5804 88.5983 52.712 88.9153 52.712 89.2458C52.712 89.5763 52.5804 89.8933 52.346 90.1272C52.1117 90.3611 51.7937 90.4929 51.462 90.4935Z"
                            fill="#E4E4E4"
                        />
                        <path
                            d="M107.197 31.4571L104.688 25.8835L104.742 25.8143C104.777 25.7709 108.238 21.4236 111.017 18.8766C111.318 18.5835 111.679 18.3594 112.076 18.2198C112.473 18.0801 112.895 18.0284 113.314 18.0682C113.903 18.1181 114.464 18.3382 114.929 18.7014C115.394 19.0645 115.743 19.555 115.932 20.1127C116.149 20.6948 116.193 21.3268 116.059 21.9332C115.925 22.5396 115.619 23.0948 115.176 23.5322L107.197 31.4571Z"
                            fill="#7B67FB"
                        />
                        <path
                            d="M109.464 13.1373C112.472 13.1373 114.911 10.7079 114.911 7.71099C114.911 4.71411 112.472 2.28467 109.464 2.28467C106.456 2.28467 104.018 4.71411 104.018 7.71099C104.018 10.7079 106.456 13.1373 109.464 13.1373Z"
                            fill="#FFB8B8"
                        />
                        <path
                            d="M101.003 104.113H98.7514L97.6797 95.4609H101.003V104.113Z"
                            fill="#FFB8B8"
                        />
                        <path
                            d="M102.191 108H100.577L100.288 106.481L99.5502 108H95.2675C95.0622 108 94.8623 107.934 94.6969 107.813C94.5316 107.692 94.4094 107.521 94.3483 107.326C94.2872 107.131 94.2904 106.921 94.3573 106.728C94.4243 106.535 94.5515 106.368 94.7204 106.251L98.1405 103.898V102.363L101.738 102.577L102.191 108Z"
                            fill="#2F2E41"
                        />
                        <path
                            d="M129.224 102.207L127.132 103.037L122.924 95.3931L126.012 94.1685L129.224 102.207Z"
                            fill="#FFB8B8"
                        />
                        <path
                            d="M131.771 105.381L130.271 105.976L129.439 104.671L129.317 106.354L125.338 107.932C125.148 108.008 124.938 108.021 124.739 107.969C124.54 107.917 124.364 107.804 124.234 107.645C124.105 107.486 124.03 107.29 124.021 107.086C124.011 106.881 124.067 106.679 124.181 106.509L126.485 103.063L125.915 101.636L129.337 100.509L131.771 105.381Z"
                            fill="#2F2E41"
                        />
                        <path
                            d="M101.87 100.138H97.2129L98.5001 78.3382L97.5591 63.5615L97.5611 63.5497L101.071 42.3681C101.347 40.6978 102.093 39.1398 103.222 37.8751L103.265 37.8271L115.01 40.7171L116.191 43.3997C120.223 45.7891 121.497 59.2625 121.549 59.8364L123.256 76.6326L127.432 97.218L124.492 98.7954L124.442 98.6978L115.158 80.3058L109.295 60.9102L105.541 78.3666L101.87 100.138Z"
                            fill="#2F2E41"
                        />
                        <path
                            d="M116.211 7.96649C116.018 6.70519 115.816 5.43332 115.364 4.24568C114.911 3.05803 114.181 1.94563 113.134 1.27284C111.476 0.207659 109.33 0.413096 107.472 1.02582C106.034 1.49967 104.631 2.21989 103.618 3.37776C102.604 4.53564 102.035 6.1878 102.412 7.70176C104.394 7.23512 106.377 6.76848 108.359 6.30183L108.142 6.45461C108.793 6.77798 109.362 7.24484 109.805 7.81977C110.247 8.39469 110.553 9.06256 110.698 9.77268C110.84 10.4914 110.819 11.2326 110.638 11.9425C110.457 12.6525 110.12 13.3135 109.651 13.8777C111.003 13.3998 112.355 12.922 113.707 12.4442C114.542 12.1492 115.435 11.8094 115.93 11.0529C116.504 10.1758 116.371 9.01322 116.211 7.96649Z"
                            fill="#2F2E41"
                        />
                        <path
                            d="M0.285547 107.948H149.714C149.79 107.948 149.863 107.918 149.916 107.865C149.97 107.811 150 107.739 150 107.663C150 107.588 149.97 107.516 149.916 107.462C149.863 107.409 149.79 107.379 149.714 107.379H0.285547C0.209814 107.379 0.137187 107.409 0.0836354 107.462C0.0300841 107.516 0 107.588 0 107.663C0 107.739 0.0300841 107.811 0.0836354 107.865C0.137187 107.918 0.209814 107.948 0.285547 107.948Z"
                            fill="#CCCCCC"
                        />
                        <path
                            d="M65.8915 40.2941H99.6879C100.015 40.2924 100.335 40.2058 100.619 40.0428C100.902 39.8799 101.139 39.6461 101.306 39.3642C101.472 39.0823 101.564 38.7619 101.571 38.4341C101.578 38.1063 101.5 37.7823 101.345 37.4935L84.4553 5.99878C84.2934 5.69686 84.0531 5.44457 83.7598 5.26871C83.4666 5.09285 83.1313 5 82.7897 5C82.4481 5 82.1128 5.09285 81.8196 5.26871C81.5263 5.44457 81.286 5.69686 81.1241 5.99878L64.226 37.4935C64.0709 37.7829 63.9932 38.1077 64.0005 38.4362C64.0078 38.7647 64.0998 39.0856 64.2676 39.3678C64.4354 39.6499 64.6732 39.8835 64.9578 40.0459C65.2425 40.2083 65.5642 40.2938 65.8915 40.2941ZM84.9401 34.5391H80.6394V30.2249H84.9401V34.5391ZM84.9401 27.3474H80.6394V18.7169H84.9401V27.3474Z"
                            fill="#F9C153"
                        />
                        <path
                            d="M109.286 28.0215L105.822 25.8188L101.27 30.3732L92.6777 38.337C92.2129 38.2339 91.7275 38.2737 91.2859 38.4514C90.8443 38.629 90.4672 38.936 90.2045 39.3318C89.9419 39.7275 89.806 40.1934 89.8149 40.6678C89.8237 41.1422 89.977 41.6027 90.2543 41.9884C90.5316 42.3742 90.9199 42.6669 91.3679 42.828C91.8159 42.9891 92.3024 43.0109 92.763 42.8905C93.2237 42.7702 93.6368 42.5133 93.9478 42.1539C94.2587 41.7946 94.4528 41.3496 94.5044 40.8779L103.98 33.8024L109.286 28.0215Z"
                            fill="#FFB8B8"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_2118_17672">
                            <rect width="150" height="108" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
            ),
        };
    }

    if (notificationType === 'import-success') {
        return {
            Icon: <IconImportSuccess />,
        };
    }

    if (notificationType === 'import-fail') {
        return {
            Icon: <IconImportFails />,
        };
    }

    return {
        Icon: <IconSuccess />,
    };
}

function Notification({ icon, title, description, type, onClose }) {
    const { Icon } = getIcon(type);
    const { t } = useTranslation();

    return (
        <div className="bg-[#00000073] w-full h-full flex justify-center pt-24">
            <Base
                className="h-max pb-6 relative"
                initial={{ opacity: 0, scale: 0.8, x: 300 }} // animate from
                animate={{ opacity: 1, scale: 1, x: 0 }} // animate to
                exit={{ opacity: 0, scale: 0.8, x: 300 }} // animate exit
                transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 40,
                }}
            >
                <div className="py-4 px-2 text-center w-full">
                    <div className="pt-12">{icon || Icon}</div>
                    <div>
                        <Title className="py-1 whitespace-pre-line">
                            {title}
                        </Title>
                        <Description className="whitespace-pre-line">
                            {description}
                        </Description>
                    </div>

                    <Button
                        hidden={type !== 'error' && type !== 'import-fail'}
                        type="primary"
                        onClick={onClose}
                        size="large"
                        className="mt-4 px-12 pointer-events-auto"
                    >
                        {t('common.Close')}
                    </Button>
                </div>
            </Base>
        </div>
    );
}

export default Notification;
